import React, { Fragment } from "react";
import { useIntl } from "react-intl";

import { ButtonLinkIcon } from "@components/Button";
import Card from "@components/Card";
import DataTable from "@components/DataTableV2/DataTable";
import Div from "@components/Div";
import { H3, H4 } from "@components/Heading";
import Icon from "@components/Icon";
import ProgressSpinner from "@components/ProgressSpinner";
import Skeleton from "@components/Skeleton";
import { Text, TextMediumWeight } from "@components/Text";

import {
  useDetailsTabActions,
  useDetailsTabInformation,
} from "./DetailsTab.hooks";

import { ActivityLogList } from "./components";

const DetailsTab = () => {
  const {
    dataSourceType,
    changeStatusButtonLabel,
    cardDetails,
    reasonsForManual,
    allDocumentsConfig,
    activityLogData,
    isLoading: isLoadingActivityLog,
  } = useDetailsTabInformation();
  const {
    hasWriteDataSourcesPermission,
    isLoading,
    handleOpenConfirmDialog,
    handleUpdateStatus,
  } = useDetailsTabActions();
  const { formatMessage, messages } = useIntl();

  return (
    <Div display="flex" flexDirection="column" gridGap={4} pb={4}>
      {isLoading && <ProgressSpinner />}

      <Div
        width={1}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["flex-start", "flex-start", "center"]}
        justifyContent="space-between"
        gridGap={2}
      >
        <H3 textTransform="none">{messages.title_data_source_details}</H3>

        {hasWriteDataSourcesPermission && (
          <Div
            display="flex"
            justifyContent={["space-between", "space-between", "flex-end"]}
            gridGap="10px"
          >
            <ButtonLinkIcon p={0} onClick={handleOpenConfirmDialog}>
              <Icon name="restart" mr={1} color="var(--turquoise)" />

              <TextMediumWeight>
                {messages.label_recreate_data_source}
              </TextMediumWeight>
            </ButtonLinkIcon>

            <ButtonLinkIcon p={0} onClick={handleUpdateStatus}>
              <Icon name="pen" mr={1} color="var(--turquoise)" />

              <TextMediumWeight>{changeStatusButtonLabel}</TextMediumWeight>
            </ButtonLinkIcon>
          </Div>
        )}
      </Div>

      <Card p={3} borderRadius="10px">
        <Div display="flex" flexDirection="column" gridGap={3}>
          <Div display="flex" gridGap="12px">
            <H4>{dataSourceType}</H4>
          </Div>

          <Div width={1} height="1px" backgroundColor="var(--grey-lightest)" />

          <Div
            display="flex"
            flexDirection={["column", "column", "row", "row"]}
            flexWrap={["nowrap", "nowrap", "wrap", "wrap"]}
            gridGap={3}
          >
            {cardDetails.map(({ labelKey, value }) => (
              <Div key={labelKey} flex="1 1 40%">
                <TextMediumWeight>
                  {formatMessage({ id: labelKey })}: <Text>{value}</Text>
                </TextMediumWeight>
              </Div>
            ))}
          </Div>
        </Div>
      </Card>

      {reasonsForManual.length > 0 && (
        <Card p={3} borderRadius="10px">
          <Div display="flex" flexDirection="column" gridGap={3}>
            <H4>{messages.title_reasons_for_manual}</H4>

            {reasonsForManual.map(reason => (
              <Fragment key={reason.filename ?? reason}>
                <Div
                  width={1}
                  height="1px"
                  backgroundColor="var(--grey-lightest)"
                />

                <Div
                  display="flex"
                  flexDirection={["column", "column", "row", "row"]}
                  flexWrap={["nowrap", "nowrap", "wrap", "wrap"]}
                  gridGap={3}
                >
                  <Div flex="1 1 100%">
                    <TextMediumWeight>
                      {messages.label_reason}:
                      <Text ml={1}>{reason.reason ?? reason}</Text>
                    </TextMediumWeight>
                  </Div>

                  {Boolean(reason.filename) && (
                    <Div flex="1 1 40%">
                      <TextMediumWeight>
                        {messages.label_filename}:
                        <Text ml={1}>{reason.filename}</Text>
                      </TextMediumWeight>
                    </Div>
                  )}

                  {Boolean(reason.doctype) && (
                    <Div flex="1 1 40%">
                      <TextMediumWeight>
                        {messages.label_doctype}:
                        <Text ml={1}>{reason.doctype}</Text>
                      </TextMediumWeight>
                    </Div>
                  )}
                </Div>
              </Fragment>
            ))}
          </Div>
        </Card>
      )}

      {allDocumentsConfig !== null && (
        <Div display="flex" flexDirection="column" gridGap={3}>
          <H3 textTransform="none">{messages.title_all_documents}</H3>

          <DataTable config={allDocumentsConfig} />
        </Div>
      )}

      <Div display="flex" flexDirection="column" gridGap={3}>
        <H3 textTransform="none">{messages.activity_log}</H3>

        {isLoadingActivityLog ? (
          <Skeleton width="100%" height="300px" borderRadius="10px" />
        ) : (
          <ActivityLogList activityLogData={activityLogData} />
        )}
      </Div>
    </Div>
  );
};

export default DetailsTab;
